import React, { useContext, useEffect, useCallback } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

// components
import {
    BodyMedium,
    BodySmall,
    ButtonDarkLarge,
    ButtonPrimaryLarge,
    DivWithShadowAlternate,
    H3,
} from '@react/styledComponents';

// functionality
import { EventRegistrationContext } from '../EventRegistrationProvider';
import { LoadingContext } from '@context/LoadingProvider';
import { addEventRegistrationToCart, addMembershipToCart, membershipYears } from '@general/helpers/cart.helpers';
import { formatMoney } from '@general/helpers/currency.helpers';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useEventFromAPI from '@hooks/apiLegacy/useEventFromAPI';
import useMemberDuesEligibilityFromAPI from '@hooks/apiLegacy/useMemberDuesEligibilityFromAPI';
import useEventPrices from '@hooks/useEventPrices';
import useViewer from '@hooks/useViewer';

const EventRegistrationPricing = () => {
    // ------------------------------
    // Hooks
    // ------------------------------

    const { registrants } = useContext(EventRegistrationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const history = useHistory();
    const match = useRouteMatch();
    const eventCode = match.params?.code;

    const { viewer } = useViewer();
    const { cart } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { event } = useEventFromAPI({ code: eventCode });
    const { nonMemberPrice, memberPrice } = useEventPrices({ eventCode });
    const { duesOneYear } = useMemberDuesEligibilityFromAPI({ id: registrants[0].recordId });

    // ------------------------------
    // Effects
    // ------------------------------

    useEffect(() => {
        setIsLoading(true);
    }, [setIsLoading]);

    useEffect(() => {
        if (getIsReadyToRender()) {
            setIsLoading(false);
        }
    }, [getIsReadyToRender, setIsLoading, event, nonMemberPrice, memberPrice, duesOneYear]);

    // ------------------------------
    // Functions
    // ------------------------------

    const addToCartAndContinue = async ({ includeMembership }) => {
        setIsLoading(true);

        if (includeMembership) {
            await addMembershipToCart({
                cart,
                memberId: registrants[0].recordId,
                viewerId: viewer.recordId,
                years: membershipYears.ONE,
            });

            await addEventRegistrationToCart({
                cart,
                event,
                memberId: registrants[0].recordId,
                viewerId: viewer.recordId,
            });
        } else {
            await addEventRegistrationToCart({
                cart,
                event,
                memberId: registrants[0].recordId,
                viewerId: viewer.recordId,
            });
        }

        setIsLoading(false);
        history.push(`/event-registration/${eventCode}/info`);
    };

    const getIsReadyToRender = useCallback(() => {
        return !!event && !!nonMemberPrice && !!memberPrice && !!duesOneYear;
    }, [event, nonMemberPrice, memberPrice, duesOneYear]);

    // ------------------------------
    // Render
    // ------------------------------

    if (!getIsReadyToRender()) {
        return null;
    }

    return (
        <div>
            <H3>Welcome back, {registrants[0].firstName}!</H3>
            <BodyMedium className="mt-1.125rem">Please select how you want to register for this event.</BodyMedium>

            <DivWithShadowAlternate className="flex items-center justify-between mt-2.5rem p-1.5rem">
                <ButtonPrimaryLarge onClick={() => addToCartAndContinue({ includeMembership: true })}>
                    Become a Member & Register
                </ButtonPrimaryLarge>

                <div className="flex items-center text-dark-gray">
                    <BodySmall>Event Price + Membership:</BodySmall>
                    <BodyMedium className="ml-0.5rem">{formatMoney(memberPrice + duesOneYear.price)}</BodyMedium>
                </div>
            </DivWithShadowAlternate>

            <DivWithShadowAlternate className="flex items-center justify-between mt-1rem p-1.5rem">
                <ButtonDarkLarge onClick={() => addToCartAndContinue({ includeMembership: false })}>
                    Register as Non-Member
                </ButtonDarkLarge>

                <div className="flex items-center text-dark-gray">
                    <BodySmall>Event Price for Non-Members: </BodySmall>
                    <BodyMedium className="ml-0.5rem">{formatMoney(nonMemberPrice)}</BodyMedium>
                </div>
            </DivWithShadowAlternate>
        </div>
    );
};

export default EventRegistrationPricing;
